/* 20261105 신규: 로그인 전 메인(Info) 탭 본문을 정적 HTML({landing}/pages/{pageName}.html)로 불러와 iframe으로 표시
   - 디자이너가 landing 폴더의 HTML/CSS만 수정하면 빌드 없이 반영됨
   - 로컬 서버는 클라우드의 파일을 불러오므로(다른 도메인) iframe 내부를 직접 읽지 않고 메시지로만 통신
     · 페이지가 "준비" 신호(HICLASS_LANDING_READY)를 보내면 표시, 일정 시간 안에 신호가 없으면 기존 React 컴포넌트(fallback) 표시
     · 페이지가 보내는 높이 신호(HICLASS_LANDING_HEIGHT)로 iframe 높이 조절
     · 다운로드 버전 정보는 React가 iframe으로 전달(HICLASS_DOWNLOAD_INFO) */
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import ConstData from "../../../lib/ConstData";
import { getLandingBaseUrl, getLandingOrigin, LANDING_CACHE_KEY } from "../../../lib/LandingPath";

const MSG_READY = "HICLASS_LANDING_READY";
const MSG_HEIGHT = "HICLASS_LANDING_HEIGHT";
const MSG_DOWNLOAD_INFO = "HICLASS_DOWNLOAD_INFO";

const READY_TIMEOUT_MS = 4000;     // 이 시간 안에 준비 신호가 없으면 기존 화면 표시 (파일 없음, 서버 연결 불가 등)

const WebHiClassInfoHtml = ({ pageName, download_info, handleSelectedFunc, fallback }) => {
    const iframeRef = useRef(null);
    const downloadInfoRef = useRef(download_info);
    const handleSelectedFuncRef = useRef(handleSelectedFunc);

    const [pageStatus, setPageStatus] = useState("loading");    // loading | ok | fallback
    const [frameHeight, setFrameHeight] = useState(0);

    downloadInfoRef.current = download_info;
    handleSelectedFuncRef.current = handleSelectedFunc;

    // 20261105 현재 앱 언어(브라우저 언어 기준, 예: "en-US" → "en")를 랜딩 페이지에 전달
    const { i18n } = useTranslation();
    const lang = String(i18n.language || "ko").substring(0, 2).toLowerCase();

    const landingOrigin = getLandingOrigin();
    // 20261105 테스트/개발 서버(IS_DEV_VALUE)면 dev=1 전달 → 페이지에서 테스트용 이미지(data-test-src)/스타일(data-hc-dev) 적용
    const pageUrl = getLandingBaseUrl() + "pages/" + pageName + ".html?lang=" + encodeURIComponent(lang) + (ConstData.IS_DEV_VALUE ? "&dev=1" : "") + "&t=" + LANDING_CACHE_KEY;

    // 20261105 iframe으로 다운로드 정보 전달 (랜딩 파일 서버로만 전송)
    const postDownloadInfo = () => {
        const iframe = iframeRef.current;

        if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({
                type: MSG_DOWNLOAD_INFO,
                downloadInfo: downloadInfoRef.current ? downloadInfoRef.current : null,
                downloadBaseUrl: ConstData.CLOUD_HOST_ADD + ConstData.DOWNLOAD_DIR
            }, landingOrigin);
        }
    };

    // 20261105 페이지가 바뀌면 준비 신호를 다시 기다림
    useEffect(() => {
        let isReady = false;

        setPageStatus("loading");
        setFrameHeight(0);

        const timer = setTimeout(() => {
            if (!isReady) {
                console.log("WebHiClassInfoHtml - landing page not ready => show original. ", pageUrl);
                setPageStatus("fallback");
            }
        }, READY_TIMEOUT_MS);

        const onMessage = (event) => {
            const iframe = iframeRef.current;

            // 우리가 띄운 iframe 에서, 랜딩 파일 서버 출처로 온 메시지만 처리
            if (!iframe || event.source !== iframe.contentWindow || event.origin !== landingOrigin) return;

            const data = event.data;
            if (!data || typeof data !== "object") return;

            if (data.type === MSG_READY) {
                isReady = true;
                clearTimeout(timer);
                setPageStatus("ok");

                // 다운로드 표시 위치가 있는 페이지면 기존 화면과 동일하게 서버에서 버전 정보 요청
                if (data.hasDownloadInfo && handleSelectedFuncRef.current) {
                    handleSelectedFuncRef.current({ selectedFunc: "GET_DOWNLOAD_INFO" });
                }

                postDownloadInfo();
            }

            if (data.type === MSG_READY || data.type === MSG_HEIGHT) {
                const height = Number(data.height);

                if (height > 0) {
                    setFrameHeight(Math.ceil(height));
                }
            }
        };

        window.addEventListener("message", onMessage);

        return () => {
            clearTimeout(timer);
            window.removeEventListener("message", onMessage);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageUrl]);

    // 20261105 다운로드 정보가 서버에서 도착하면 다시 전달
    useEffect(() => {
        if (pageStatus === "ok") {
            postDownloadInfo();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [download_info, pageStatus]);

    if (pageStatus === "fallback") {
        return fallback ? fallback : null;
    }

    return (
        <div className="d-flex flex-column w-100" style={{ minHeight: frameHeight > 0 ? 'unset' : '50vh' }}>
            <iframe
                key={pageUrl}
                ref={iframeRef}
                src={pageUrl}
                title={"hiclasstv-info-" + pageName}
                scrolling="no"
                style={{
                    display: 'block',
                    width: '100%',
                    height: frameHeight > 0 ? frameHeight + 'px' : '50vh',
                    border: 'none',
                    overflow: 'hidden',
                    visibility: pageStatus === "ok" ? 'visible' : 'hidden'     // 준비 신호 전에는 숨김
                }}
            />
        </div>
    );
};

export default WebHiClassInfoHtml;
